// Ultra pacing algorithm extracted from legacy implementation.

/**
 * Cohort performance drop data based on UTMB/CCC/OCC research
 * Structure: cohort number -> [OCC_56km, CCC_101km, UTMB_171km] drops as decimals
 */
const COHORT_PERFORMANCE_DROPS = {
  1: [-0.12, -0.20, -0.32], // Elite
  2: [-0.16, -0.25, -0.37], // Sub-elite
  3: [-0.20, -0.29, -0.41], // Competitive
  4: [-0.24, -0.33, -0.45], // Strong
  5: [-0.26, -0.36, -0.48], // Mid-pack+
  6: [-0.28, -0.39, -0.51], // Mid-pack
  7: [-0.32, -0.43, -0.55], // Mid-pack-
  8: [-0.36, -0.47, -0.59], // Back-of-pack+
  9: [-0.42, -0.52, -0.64], // Back-of-pack
  10: [-0.50, -0.58, -0.70] // Survival mode
};

/**
 * Target Intensity Factors by cohort (what % of AT pace they should average)
 */
const COHORT_TARGET_IF = {
  1: 0.735, 2: 0.72, 3: 0.71, 4: 0.70, 5: 0.69,
  6: 0.68, 7: 0.67, 8: 0.66, 9: 0.65, 10: 0.635
};

/**
 * Cohort names for display
 */
export const COHORT_NAMES = {
  1: 'Elite', 2: 'Sub-Elite', 3: 'Competitive', 4: 'Strong', 5: 'Mid-Pack+',
  6: 'Mid-Pack', 7: 'Mid-Pack-', 8: 'Back-of-Pack+', 9: 'Back-of-Pack', 10: 'Survival Mode'
};

export function formatPace(paceMinPerKm) {
  const minutes = Math.floor(paceMinPerKm);
  const seconds = Math.round((paceMinPerKm - minutes) * 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function formatTime(minutes) {
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  const secs = Math.round((minutes % 1) * 60);

  if (hours === 0) {
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  } else if (secs === 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  } else {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
}

function paceToSpeed(paceMinPerKm) {
  return 60 / paceMinPerKm; // km/h
}

function speedToPace(speedKmH) {
  return 60 / speedKmH; // min/km
}

/**
 * Assign runner to performance cohort based on AT pace
 */
export function assignCohort(atPaceMinPerKm) {
  if (atPaceMinPerKm <= 3.25) return 1; // Elite: ≤3:15/km
  else if (atPaceMinPerKm <= 3.75) return 2; // Sub-elite: 3:15-3:45/km
  else if (atPaceMinPerKm <= 4.25) return 3; // Competitive: 3:45-4:15/km
  else if (atPaceMinPerKm <= 4.75) return 4; // Strong: 4:15-4:45/km
  else if (atPaceMinPerKm <= 5.25) return 5; // Mid-pack+: 4:45-5:15/km
  else if (atPaceMinPerKm <= 5.75) return 6; // Mid-pack: 5:15-5:45/km
  else if (atPaceMinPerKm <= 6.25) return 7; // Mid-pack-: 5:45-6:15/km
  else if (atPaceMinPerKm <= 6.75) return 8; // Back-of-pack+: 6:15-6:45/km
  else if (atPaceMinPerKm <= 7.50) return 9; // Back-of-pack: 6:45-7:30/km
  else return 10; // Survival mode: >7:30/km
}

/**
 * Get performance drop for a given distance and cohort using logarithmic interpolation
 */
function getPerformanceDrop(distanceKm, cohort) {
  const drops = COHORT_PERFORMANCE_DROPS[cohort];
  const distances = [56, 101, 171]; // OCC, CCC, UTMB reference points

  // For distances shorter than OCC (56km), linear scaling
  if (distanceKm <= distances[0]) {
    return drops[0] * (distanceKm / distances[0]);
  }

  // Logarithmic interpolation between reference points
  if (distanceKm <= distances[1]) {
    const t = Math.log(distanceKm / distances[0]) / Math.log(distances[1] / distances[0]);
    return drops[0] + t * (drops[1] - drops[0]);
  }

  if (distanceKm <= distances[2]) {
    const t = Math.log(distanceKm / distances[1]) / Math.log(distances[2] / distances[1]);
    return drops[1] + t * (drops[2] - drops[1]);
  }

  // Extrapolation beyond UTMB (use with caution, cap at -80%)
  const excessDistance = distanceKm - distances[2];
  const additionalDrop = (excessDistance / distances[2]) * 0.15 * Math.abs(drops[2]);
  return Math.max(drops[2] - additionalDrop, -0.80);
}

/**
 * Calculate elevation fatigue factor based on vertical gain per km
 * @returns {number} Multiplier (0.88-1.00) where lower = more fatigue
 */
function elevationFatigueFactor(totalElevationGainM, distanceKm) {
  const elevationPerKm = totalElevationGainM / distanceKm;

  if (elevationPerKm < 20) return 1.00; // Flat/rolling
  else if (elevationPerKm < 40) return 0.98; // Moderate vertical
  else if (elevationPerKm < 60) return 0.95; // High vertical
  else if (elevationPerKm < 80) return 0.92; // Very high vertical (UTMB-level)
  else return 0.88; // Extreme vertical (>80m/km)
}

/**
 * Calculate fatigue-based speed degradation (applies to speed, not pace).
 * Returns negative value, e.g. -0.15 = 15% speed reduction.
 */
function calculateCurrentFatigueDrop(fractionComplete, cohortPerformanceDrop) {
  let drop = 0;

  // Phase 1: Settling in (0-25%) - Minimal drop, linear
  if (fractionComplete < 0.25) {
    drop = cohortPerformanceDrop * 0.15 * (fractionComplete / 0.25);
  }
  // Phase 2: Steady state (25-60%) - Linear drop
  else if (fractionComplete < 0.60) {
    const phase1Drop = cohortPerformanceDrop * 0.15;
    const phase2Fraction = (fractionComplete - 0.25) / 0.35;
    const phase2Drop = cohortPerformanceDrop * 0.30 * phase2Fraction;
    drop = phase1Drop + phase2Drop;
  }
  // Phase 3: Accumulating fatigue (60-85%) - Accelerated drop
  else if (fractionComplete < 0.85) {
    const cumulativeDrop = cohortPerformanceDrop * 0.45;
    const phase3Fraction = (fractionComplete - 0.60) / 0.25;
    const phase3Drop = cohortPerformanceDrop * 0.35 * Math.pow(phase3Fraction, 1.5);
    drop = cumulativeDrop + phase3Drop;
  }
  // Phase 4: Deep reserves (85-100%) - Steep drop
  else {
    const cumulativeDrop = cohortPerformanceDrop * 0.80;
    const phase4Fraction = (fractionComplete - 0.85) / 0.15;
    const phase4Drop = cohortPerformanceDrop * 0.20 * Math.pow(phase4Fraction, 2);
    drop = cumulativeDrop + phase4Drop;
  }

  return drop;
}

/**
 * Downhill reality constraints to prevent unrealistic pace predictions.
 * Maximum downhill benefit by runner experience and gradient.
 */
const DOWNHILL_LIMITS = {
  elite: {
    5: 0.82,
    10: 0.75,
    15: 0.80,
    20: 0.85,
    25: 0.90,
    30: 0.95,
    35: 1.00
  },
  competitive: {
    5: 0.88,
    10: 0.83,
    15: 0.88,
    20: 0.93,
    25: 0.98,
    30: 1.02,
    35: 1.05
  },
  recreational: {
    5: 0.92,
    10: 0.88,
    15: 0.95,
    20: 1.00,
    25: 1.05,
    30: 1.10,
    35: 1.15
  }
};

function getRunnerExperienceLevel(cohort) {
  if (cohort <= 3) return 'elite';
  else if (cohort <= 6) return 'competitive';
  else return 'recreational';
}

function applyDownhillRealityCheck(rawTerrainPace, gradient, currentFlatPace, experienceLevel, fatigueLevel = 0) {
  // Only apply to significant downhills
  if (gradient >= -5) return rawTerrainPace;

  const gradientAbs = Math.abs(gradient);
  const limits = DOWNHILL_LIMITS[experienceLevel] || DOWNHILL_LIMITS.recreational;

  let benefitMultiplier = 1.00;
  if (gradientAbs >= 35) benefitMultiplier = limits[35];
  else if (gradientAbs >= 30) benefitMultiplier = limits[30];
  else if (gradientAbs >= 25) benefitMultiplier = limits[25];
  else if (gradientAbs >= 20) benefitMultiplier = limits[20];
  else if (gradientAbs >= 15) benefitMultiplier = limits[15];
  else if (gradientAbs >= 10) benefitMultiplier = limits[10];
  else if (gradientAbs >= 5) benefitMultiplier = limits[5];

  // Apply fatigue penalty (tired runners are slower on downhills)
  const fatiguePenalty = 1 + (fatigueLevel / 100) * 0.3; // Up to 30% less benefit
  benefitMultiplier *= fatiguePenalty;

  const maxAllowedPace = currentFlatPace * benefitMultiplier;
  return Math.max(rawTerrainPace, maxAllowedPace);
}

function getUltraGradientMultiplier(gradient, fractionComplete) {
  let phase;
  if (fractionComplete < 0.25) phase = 'early';
  else if (fractionComplete < 0.75) phase = 'mid';
  else phase = 'late';

  const multipliers = {
    early: {
      0.20: 1.00, 0.15: 1.00, 0.10: 1.00, 0.05: 1.00,
      0.00: 1.00,
      '-0.05': 1.00, '-0.10': 1.00, '-0.15': 1.00
    },
    mid: {
      0.20: 1.15, 0.15: 1.12, 0.10: 1.08, 0.05: 1.04,
      0.00: 1.05,
      '-0.05': 1.08, '-0.10': 1.12, '-0.15': 1.18
    },
    late: {
      0.20: 1.35, 0.15: 1.28, 0.10: 1.20, 0.05: 1.10,
      0.00: 1.15,
      '-0.05': 1.20, '-0.10': 1.30, '-0.15': 1.45
    }
  };

  const gradientEntries = Object.entries(multipliers[phase]).map(([key, value]) => [parseFloat(key), key, value]);
  gradientEntries.sort((a, b) => a[0] - b[0]);

  let closestEntry = gradientEntries[0];
  let minDiff = Math.abs(gradient - closestEntry[0]);

  for (const entry of gradientEntries) {
    const diff = Math.abs(gradient - entry[0]);
    if (diff < minDiff) {
      minDiff = diff;
      closestEntry = entry;
    }
  }

  return closestEntry[2];
}

function shouldPowerHike(gradient, distanceKm, cohort, predictedPaceMinPerKm) {
  // Only consider hiking for positive gradients
  if (gradient < 0.12) return false;

  const durationMinutes = distanceKm * predictedPaceMinPerKm;

  if (gradient >= 0.25) return true;

  if (gradient >= 0.20) {
    if (cohort >= 8) return true;
    if (cohort >= 4 && durationMinutes > 8) return true;
    if (cohort <= 3 && durationMinutes > 10) return true;
  }

  if (gradient >= 0.15) {
    if (cohort >= 8) return true;
    if (cohort >= 4 && durationMinutes > 15) return true;
    if (cohort <= 3 && durationMinutes > 20) return true;
  }

  return false;
}

function calculateWalkPace(atPaceMinPerKm, gradient) {
  const baseWalkPace = atPaceMinPerKm * 2.2;

  let walkAdjustment;
  if (gradient >= 0.25) walkAdjustment = 2.0;
  else if (gradient >= 0.20) walkAdjustment = 1.7;
  else if (gradient >= 0.15) walkAdjustment = 1.4;
  else if (gradient >= 0.12) walkAdjustment = 1.25;
  else walkAdjustment = 1.0;

  return baseWalkPace * walkAdjustment;
}

/**
 * Minetti polynomial for cost of running on gradients (from GAP research)
 * @param {number} gradient Decimal (e.g. 0.10 for 10%)
 */
export function costOfRunning(gradient) {
  const g = gradient;
  return 155.4 * Math.pow(g, 5) - 30.4 * Math.pow(g, 4) - 43.3 * Math.pow(g, 3) +
    46.3 * Math.pow(g, 2) + 19.5 * g + 3.6;
}

/**
 * Main ultra pacing calculation function.
 * @param {number} atDistanceKm Distance runner can cover in 1 hour
 * @param {number} totalDistanceKm Total course distance in km
 * @param {number} totalElevationGainM Total elevation gain in meters
 * @param {Array} sections Array of section objects (expects meters + avgGradient %)
 */
export function calculateUltraPacing(atDistanceKm, totalDistanceKm, totalElevationGainM, sections) {
  const atPaceMinPerKm = 60 / atDistanceKm;
  const atSpeedKmH = paceToSpeed(atPaceMinPerKm);

  const cohort = assignCohort(atPaceMinPerKm);
  const experienceLevel = getRunnerExperienceLevel(cohort);

  const basePerformanceDrop = getPerformanceDrop(totalDistanceKm, cohort);

  const elevationFactor = elevationFatigueFactor(totalElevationGainM, totalDistanceKm);
  const adjustedPerformanceDrop = basePerformanceDrop * elevationFactor;

  const targetIF = COHORT_TARGET_IF[cohort];
  const targetFlatSpeed = atSpeedKmH * targetIF;
  const targetAvgPace = speedToPace(targetFlatSpeed);

  let cumulativeTimeMinutes = 0;
  let cumulativeDistanceKm = 0;
  const sectionPacing = [];

  for (const section of sections) {
    cumulativeDistanceKm += section.totalDistance / 1000;
    const fractionComplete = cumulativeDistanceKm / totalDistanceKm;

    const currentFatigueDrop = calculateCurrentFatigueDrop(fractionComplete, adjustedPerformanceDrop);
    const currentFlatSpeed = targetFlatSpeed * (1 + currentFatigueDrop);
    const currentFlatPace = speedToPace(currentFlatSpeed);

    const gapFactor = costOfRunning(section.averageGradient / 100) / costOfRunning(0);
    const rawTerrainSpeed = currentFlatSpeed / gapFactor;
    let rawTerrainPace = speedToPace(rawTerrainSpeed);

    let ultraGradientMult = 1.0;
    if (section.averageGradient > 0) {
      ultraGradientMult = getUltraGradientMultiplier(section.averageGradient / 100, fractionComplete);
    } else if (section.averageGradient < -10 && fractionComplete > 0.6) {
      const quadDamageMult = 1 + (fractionComplete - 0.6) * 0.5;
      ultraGradientMult = quadDamageMult;
    }

    rawTerrainPace = rawTerrainPace * ultraGradientMult;

    const fatigueLevel = Math.abs(currentFatigueDrop) * 100;
    let sectionPacePerKm = applyDownhillRealityCheck(
      rawTerrainPace,
      section.averageGradient,
      currentFlatPace,
      experienceLevel,
      fatigueLevel
    );

    const distanceKm = section.totalDistance / 1000;
    const shouldHike = shouldPowerHike(section.averageGradient / 100, distanceKm, cohort, sectionPacePerKm);

    let strategy;
    let realityAdjustment = sectionPacePerKm / rawTerrainPace;

    if (shouldHike) {
      sectionPacePerKm = calculateWalkPace(atPaceMinPerKm, section.averageGradient / 100);
      strategy = 'hike';
      realityAdjustment = sectionPacePerKm / rawTerrainPace;
    } else if (section.averageGradient > 8 && fractionComplete > 0.6) {
      strategy = 'run-hard';
    } else if (fractionComplete > 0.85 && section.averageGradient < -10) {
      strategy = 'critical';
    } else {
      strategy = 'run';
    }

    const sectionTimeMinutes = distanceKm * sectionPacePerKm;
    cumulativeTimeMinutes += sectionTimeMinutes;

    const naiveGAPPace = targetAvgPace * gapFactor;
    const paceDifferencePercent = ((sectionPacePerKm - naiveGAPPace) / naiveGAPPace) * 100;
    const hasAlert = Math.abs(paceDifferencePercent) > 25 || realityAdjustment > 1.5;

    let pacingNotes = '';
    if (section.averageGradient < -15 && realityAdjustment > 1.5) {
      pacingNotes = 'Steep downhill: Pace slowed for safety and quad preservation';
    } else if (section.averageGradient < -10 && realityAdjustment > 1.2) {
      pacingNotes = 'Moderate downhill: Conservative pacing for sustainability';
    } else if (section.averageGradient > 20) {
      pacingNotes = 'Consider power hiking for efficiency';
    }

    sectionPacing.push({
      sectionNumber: section.sectionNumber,
      distance: distanceKm,
      gradient: section.averageGradient,
      pace: sectionPacePerKm,
      time: sectionTimeMinutes,
      cumulativeTime: cumulativeTimeMinutes,
      strategy,
      fatiguePercent: fatigueLevel,
      currentFlatPace,
      realityAdjustment,
      pacingNotes,
      alert: hasAlert ? {
        type: paceDifferencePercent > 0 ? 'slower' : 'faster',
        percent: Math.abs(paceDifferencePercent),
        message: realityAdjustment > 1.5
          ? `Downhill pace slowed ${Math.round((realityAdjustment - 1) * 100)}% for safety`
          : paceDifferencePercent > 0
            ? `Fatigue slows pace ${Math.round(Math.abs(paceDifferencePercent))}% vs. fresh GAP`
            : `Early race pacing ${Math.round(Math.abs(paceDifferencePercent))}% faster than late-race capability`
      } : null
    });
  }

  const totalTimeHours = cumulativeTimeMinutes / 60;
  const overallAvgPace = cumulativeTimeMinutes / totalDistanceKm;

  let totalGAPDistance = 0;
  for (const section of sections) {
    const gapFactor = costOfRunning(section.averageGradient / 100) / costOfRunning(0);
    totalGAPDistance += (section.totalDistance / 1000) * gapFactor;
  }
  const gapAvgPace = cumulativeTimeMinutes / totalGAPDistance;

  const strategyBreakdown = { run: 0, 'run-hard': 0, hike: 0, critical: 0 };
  for (const sp of sectionPacing) strategyBreakdown[sp.strategy] += sp.distance;

  const maxRecommendedPace = targetAvgPace * 0.95;

  const downhillSections = sectionPacing.filter(sp => sp.gradient < -10);
  const avgDownhillAdjustment = downhillSections.length > 0
    ? downhillSections.reduce((sum, sp) => sum + sp.realityAdjustment, 0) / downhillSections.length
    : 1.0;

  return {
    atPaceMinPerKm,
    atDistanceKm,
    cohort,
    cohortName: COHORT_NAMES[cohort],
    experienceLevel,
    basePerformanceDrop,
    elevationFactor,
    adjustedPerformanceDrop,
    targetIF,
    targetAvgPace,
    overallAvgPace,
    gapAvgPace,
    maxRecommendedPace,
    totalTimeMinutes: cumulativeTimeMinutes,
    totalTimeHours,
    strategyBreakdown,
    algorithmVersion: 'v2.1-gap-corrected',
    avgDownhillAdjustment,
    safetyImprovements: {
      downhillConstraints: true,
      speedBasedFatigue: true,
      realityChecks: true
    },
    sectionPacing
  };
}

export function exportUltraPacingCsv(ultraPacing) {
  let csv = 'Section,Distance(km),Gradient(%),Strategy,Pace(min/km),Time,Cumulative Time,Fatigue(%),Current Flat Pace,Reality Adjustment,Notes,Algorithm Version\n';

  for (const sp of ultraPacing.sectionPacing) {
    csv += `${sp.sectionNumber},${sp.distance.toFixed(2)},${sp.gradient.toFixed(1)},`;
    csv += `${sp.strategy},${formatPace(sp.pace)},${formatTime(sp.time)},`;
    csv += `${formatTime(sp.cumulativeTime)},${Math.round(sp.fatiguePercent)},`;
    csv += `${sp.currentFlatPace ? formatPace(sp.currentFlatPace) : 'N/A'},`;
    csv += `${sp.realityAdjustment ? sp.realityAdjustment.toFixed(2) : '1.00'},`;
    csv += `"${(sp.pacingNotes || '').replaceAll('"', '""')}",`;
    csv += `${ultraPacing.algorithmVersion || 'v1.0'}\n`;
  }

  return csv;
}

